import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CrtDisplay.css';

function CrtDisplay({ rows, typingSpeed = 18, rowDelay = 80, scrollDuration = 400 }) {
  const [visibleRows, setVisibleRows] = useState([]);
  // Which row the caret currently sits on: { section: 'header' | 'data', index }
  const [caretAt, setCaretAt] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const screenRef = useRef(null);
  const animationRef = useRef(null);
  const rowQueueRef = useRef([]);

  const startAnimation = useCallback(() => {
    if (!rows || rows.length === 0) return;

    setVisibleRows([]);
    setCaretAt(null);
    setIsComplete(false);
    rowQueueRef.current = [...rows];

    const headerRows = rows.filter(r => r.isHeader);
    const dataRows = rows.filter(r => !r.isHeader);

    const allRows = [...headerRows, ...dataRows];
    let currentRowIdx = 0;
    let charIdx = 0;
    let displayedHeaderRows = [];
    let displayedDataRows = [];

    const typeNextChar = () => {
      if (currentRowIdx >= allRows.length) {
        setIsComplete(true);
        return;
      }

      const currentRow = allRows[currentRowIdx];
      const isHeader = currentRow.isHeader;
      const fullText = currentRow.text || '';

      if (charIdx === 0) {
        if (isHeader) {
          displayedHeaderRows = [...displayedHeaderRows, { ...currentRow, text: '', key: currentRow.key || currentRowIdx }];
        } else {
          displayedDataRows = [{ ...currentRow, text: '', key: currentRow.key || currentRowIdx }, ...displayedDataRows];
        }
        setVisibleRows([...displayedHeaderRows, ...displayedDataRows]);

        if (!isHeader && screenRef.current) {
          screenRef.current.scrollTop = 0;
        }
      }

      charIdx++;
      if (isHeader) {
        displayedHeaderRows[displayedHeaderRows.length - 1] = {
          ...currentRow,
          text: fullText.slice(0, charIdx),
          key: currentRow.key || currentRowIdx
        };
      } else {
        displayedDataRows[0] = {
          ...currentRow,
          text: fullText.slice(0, charIdx),
          key: currentRow.key || currentRowIdx
        };
      }
      setVisibleRows([...displayedHeaderRows, ...displayedDataRows]);
      setCaretAt({
        section: isHeader ? 'header' : 'data',
        index: isHeader ? displayedHeaderRows.length - 1 : 0
      });

      if (charIdx >= fullText.length) {
        charIdx = 0;
        currentRowIdx++;
        animationRef.current = setTimeout(typeNextChar, rowDelay);
      } else {
        animationRef.current = setTimeout(typeNextChar, typingSpeed);
      }
    };

    animationRef.current = setTimeout(typeNextChar, 500);
  }, [rows, typingSpeed, rowDelay]);

  useEffect(() => {
    startAnimation();
    return () => {
      if (animationRef.current) clearTimeout(animationRef.current);
    };
  }, [startAnimation]);

  const headerRows = visibleRows.filter(r => r.isHeader);
  const dataRows = visibleRows.filter(r => !r.isHeader);

  // The caret is drawn as a ::after on the text span itself, so it always sits
  // flush against the last typed character rather than at a fixed position.
  const textClass = (section, index) => {
    const classes = ['crt-row__text'];
    if (caretAt && caretAt.section === section && caretAt.index === index) {
      classes.push('crt-row__text--caret');
      if (isComplete) classes.push('crt-row__text--caret-resting');
    }
    return classes.join(' ');
  };

  return (
    <div className="crt-frame">
      <div className="crt-bezel">
        <div className="crt-screen">
          <div className="crt-scanlines" />
          <div className="crt-content">
            {/* Sticky header area */}
            <div className="crt-header-fixed">
              {headerRows.map((row, i) => (
                <div
                  key={row.key ?? `h-${i}`}
                  className="crt-row crt-row--header"
                >
                  <span className={textClass('header', i)}>{row.text}</span>
                </div>
              ))}
            </div>
            {/* Scrollable data rows */}
            <div className="crt-data-scroll" ref={screenRef}>
              {dataRows.map((row, i) => (
                <div
                  key={row.key ?? i}
                  className={`crt-row ${i === dataRows.length - 1 ? 'crt-row--first' : ''}`}
                  style={{ animationDuration: `${scrollDuration}ms` }}
                >
                  <span className={textClass('data', i)}>{row.text}</span>
                </div>
              ))}
              {visibleRows.length === 0 && (
                <div className="crt-row">
                  <span className="crt-row__text crt-row__text--caret crt-row__text--caret-resting" />
                </div>
              )}
            </div>
          </div>
          <div className="crt-glow" />
        </div>
      </div>
    </div>
  );
}

export default CrtDisplay;
